import React, { useState, useRef, useCallback, memo, useMemo, useEffect } from "react";

import Box from "@mui/material/Box";
import List from "@mui/material/List";
import Modal from "@mui/material/Modal";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";

import NavbarLinks from "../NavbarLinks";
import TourStopsBlock from "../TourStopsBlock";


const ModalWrap = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "100%",
  height:'101vh',
  backgroundColor: "white",
  boxSizing: "border-box",
  overflow: "auto",
  "&:focus": {
    outline: "none",
  },
  [theme.breakpoints.up("sm")]: {
    maxWidth: "1440px",
  },
}));

const ModalHeader = styled(Box)(({ theme }) => ({
  width: "100%",
  padding: "28px 20px 0px",
  boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.2)",
  position: "sticky",
  top: 0,
  boxSizing: "border-box",
  zIndex: 2,
  backgroundColor: "#FFFFFF",
  [theme.breakpoints.up("sm")]: {
    padding: "31px 36px 0px",
  },
}));

const ModalHeaderTitle = styled(Typography)(({ theme }) => ({
  marginBottom: "29px",
  fontFamily: "Proxima Nova",
  fontWeight: "bold",
  fontSize: "26px",
  lineHeight: 1.23,
  color: "#1a1a1a",
  [theme.breakpoints.up("sm")]: {
    marginBottom: "21px",
    fontSize: "30px",
    lineHeight: 1.233,
  },
}));

const CloseButton = styled(Button)(({right}) => ({
  position: "absolute",
  top: "28px",
  right: right,
  height: '50',
  width: "68px",
  padding: "8px",
  background: "rgba(0, 0, 0, 0.05)",
  borderRadius: "5px",
  border: "none",
  fontFamily: "Proxima Nova",
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: 1.211,
  color: "#1a1a1a",
  boxSizing: "border-box",
  textTransform: "none",
  "&:hover": {
    background: "rgba(0, 0, 0, 0.05)",
  },
  "& .MuiButton-startIcon": {
    marginRight: "3px !important",
    marginLeft: "0 !important",
  },
}));

const CloseIconCustom = styled(CloseIcon)(() => ({
  fontSize: "11px !important",
}));

const Navbar = styled(List)(({ theme }) => ({
  padding: 0,
  transition: 'all 1s',
  boxSizing:'border-box',
  display: "flex",
  alignItems: "center",
  gap: "20px",
  marginLeft: '0px',
  overflowX: "scroll",
  overflowY: "hidden",
  [theme.breakpoints.up("sm")]: {
    overflow: "hidden",
  },
}));

const ModalBody = styled(Box)(({ theme }) => ({
  padding: "0 20px 44px",
  width: "100%",
  boxSizing: "border-box",
  backgroundColor: "#E5E5E5",
  [theme.breakpoints.up("sm")]: {
    padding: "0 0 90px 36px",
    width: "auto",
    marginLeft: 0,
    marginRight: 0,
  },
}));

const AddStopModal = memo(
  ({
    setClickAdd,
    stops,
    addedStopsId,
    total,
    tourDefaultPrice,
    addStation,
    open,
    handleClose,
  }) => {
    const [currentLink, setCurrentLink] = useState(
      stops?.map((el) => el.place_type)[0]
    );
    const ref = useRef([]);
    const navbar = useRef();

    const uniqStops = useMemo(() => {
      return stops.filter(item => !addedStopsId.includes(item.id))   
    }, [addedStopsId])
      
    const uniqPlaceType = stops
      ?.filter((el) => !addedStopsId?.includes(el.id))
      .map((el) => el.place_type)
      .filter((el, i, arr) => i === arr.indexOf(el))
      .sort((a,b) => a - b);
    const scrollToSection = useCallback(
      (id) => {
        ref.current[id]?.scrollIntoView({behavior:'smooth'});
      },
      [ref]
    );

    const buttonRight = useMemo(() => {
      if (document.body.offsetWidth < 400) {
        return '10px'
      } else {
        return '20px'
      }
    },[])

    
    function checkScroll() {
      if (open) {
        if (navbar.current.style.marginLeft == '-200px' && currentLink <= 4) {
          navbar.current.style.marginLeft = '0px'
        }
       if (navbar.current.clientWidth === navbar.current.scrollWidth) {
          return
        }
          if (currentLink > 3) {
            navbar.current.style.marginLeft = '-200px'
          } else {
            navbar.current.style.marginLeft = '0px'
          }
        }
      }

      useEffect(() => {
         checkScroll()
      }, [currentLink])
     

 
    return (
      <Modal open={open} onClose={handleClose} id="modal">
        {uniqPlaceType && (
          <ModalWrap>
            <ModalHeader>
              <CloseButton
                right={buttonRight}
                startIcon={<CloseIconCustom />}
                onClick={handleClose}
              >
                Close
              </CloseButton>
              <ModalHeaderTitle>Add a sightseeing stop</ModalHeaderTitle>
              <Navbar ref={navbar}>
                {uniqPlaceType.map((el) => {
                  if (!currentLink) {
                    setCurrentLink(el);
                  }
                  return (
                    <NavbarLinks
                      place={el}
                      currentLink={currentLink}
                      scrollToSection={scrollToSection}
                      setCurrentLink={setCurrentLink}
                      key={el}
                    />
                  );
                })}
              </Navbar>
            </ModalHeader>
            <ModalBody>
              {uniqPlaceType.map((el) => (
                <div
                  ref={(elem) => (ref.current[el] = elem)}
                  key={el}
                  style={{ scrollMarginTop: "7em" }}
                >
                  <TourStopsBlock
                    uniqStops={uniqStops}
                    place={el}
                    total={total}
                    tourDefaultPrice={tourDefaultPrice}
                    setClickAdd={setClickAdd}
                    addStation={addStation}
                    setCurrentLink={setCurrentLink}
                    handleClose={handleClose}
                    stops={stops}
                  />
                </div>
              ))}
            </ModalBody>
          </ModalWrap>
        )}
      </Modal>
    );
  }
);
export default AddStopModal;
